/**
 * Session security utilities for secure session management
 */

import { auth } from '../config/firebase';
import { offlineService } from '../services/offlineService';
import type { User } from 'firebase/auth';
import { appConfig } from '../config/appConfig';

/**
 * Where a tab keeps its own session id, and where the shared registry of open tabs lives.
 *
 * The split is the whole fix for S33: the ID is per tab (`sessionStorage`), the REGISTRY is
 * shared (`localStorage`). Before this there was one shared id, so a second tab looked
 * exactly like a session takeover — and signed the user out of both within 60 seconds.
 */
const TAB_SESSION_KEY = 'session_tab_id';
/**
 * Whose session the tab id belongs to.
 *
 * Reusing a tab's id across a change of USER would let one child's session identifier carry
 * over to the next on a shared classroom Chromebook — the machine this product is sold for.
 * The id is reused only when the same person is still signed in; anyone else gets a fresh
 * one. A sign-out clears both, so the ordinary case never reaches this check.
 */
const TAB_SESSION_USER_KEY = 'session_tab_user';
const TAB_REGISTRY_KEY = 'session_tabs';

/**
 * How long a tab may go without a heartbeat before it is presumed closed.
 *
 * The heartbeat runs every 60s, so this allows two missed beats plus slack. Shorter would
 * evict a backgrounded tab the browser has throttled — and on a Chromebook a tab in another
 * window is throttled routinely, which is exactly the machine this has to be right on.
 *
 * There is no reliable "tab closed" signal to use instead: `beforeunload` does not fire on a
 * crash, a force-quit, or a lid closing.
 */
const TAB_STALE_MS = 3 * 60 * 1000;

interface SessionInfo {
  userId: string;
  sessionId: string;
  loginTime: number;
  lastActivity: number;
  ipAddress?: string;
  userAgent: string;
  isActive: boolean;
}

interface SessionSettings {
  maxSessionDuration: number; // 8 hours in milliseconds
  inactivityTimeout: number; // 30 minutes in milliseconds
  maxConcurrentSessions: number; // Maximum sessions per user
}

/**
 * S234: every automatic sign-out sends the child's unsaved drafts FIRST, while the session can
 * still write them. The device clean-up runs on the auth transition (AuthContext), after the
 * session is gone — so a draft it found there could no longer be sent and stayed on a shared
 * Chromebook. Inactivity and the maximum session length are how most classroom sessions end.
 */
async function signOutKeepingWork(): Promise<void> {
  const uid = auth.value.currentUser?.uid;
  if (uid) {
    // Bounded: a write waits for the server's acknowledgement, and an expiring session on a
    // dropped network must still sign out. Five seconds, then out regardless (unsent drafts stay).
    let timer: ReturnType<typeof setTimeout> | undefined;
    await Promise.race([
      offlineService.pushDrafts(uid).catch(() => undefined),
      new Promise((resolve) => {
        timer = setTimeout(resolve, 5000);
      }),
    ]).finally(() => clearTimeout(timer));
  }
  await auth.value.signOut();
}

export class SessionSecurityManager {
  /**
   * S135: these two were literals here — 8 hours and 30 minutes — while
   * `appConfig.limits` exposed both as tunable environment variables that
   * nothing read. The values agreed with the defaults, which is why it went
   * unnoticed; an operator who set REACT_APP_INACTIVITY_TIMEOUT for a district
   * with a shared-device policy changed a config object and nothing else, and a
   * reviewer auditing `appConfig.limits` was reading a document, not a control.
   *
   * The defaults live in `appConfig` now, so there is one number per limit.
   */
  private sessionSettings: SessionSettings = {
    maxSessionDuration: appConfig.get('limits').sessionTimeout,
    inactivityTimeout: appConfig.get('limits').inactivityTimeout,
    maxConcurrentSessions: 5
  };

  private currentSession: SessionInfo | null = null;
  private inactivityTimer: NodeJS.Timeout | null = null;
  private inactivityTimerStartedAt: number | null = null;

  private initialized = false;

  constructor(options: { autoInit?: boolean } = {}) {
    // Defer initialization to avoid race condition with Firebase auth lazy proxy
    if (options.autoInit !== false && typeof window !== 'undefined') {
      // Use setTimeout(0) to ensure Firebase has initialized
      setTimeout(() => this.initializeSecurityMonitoring(), 0);
    }
  }

  /** Test seam: run the wiring the constructor defers, synchronously. */
  public init(): void {
    this.initializeSecurityMonitoring();
  }

  /**
   * Initialize security monitoring for the current session
   */
  private initializeSecurityMonitoring(): void {
    if (this.initialized) return;
    this.initialized = true;

    // Monitor authentication state changes
    try {
      auth.value.onAuthStateChanged((user: User | null) => {
      if (user) {
        this.createSession(user);
      } else {
        this.destroySession();
      }
    });

    // Monitor page visibility to handle session timeouts
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.pauseInactivityTimer();
      } else {
        this.resumeInactivityTimer();
        this.updateLastActivity();
      }
    });

    // Monitor user activity to reset inactivity timer
    ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'].forEach(event => {
      document.addEventListener(event, () => this.updateLastActivity(), { passive: true });
    });

    // Check for concurrent sessions periodically
    setInterval(() => {
      // The heartbeat comes first: a tab that is about to evaluate the limit must have
      // announced itself, or it would count only the OTHER tabs and could evict itself.
      if (this.currentSession) this.registerTab(this.currentSession.sessionId);
      this.checkConcurrentSessions();
    }, 60000); // Every minute
    } catch (error) {
      console.error('Error initializing session security:', error);
    }
  }

  /**
   * Create a new session for the authenticated user
   */
  /**
   * Every tab that is currently open, newest last, pruned of dead ones.
   *
   * Held in `localStorage` because it has to be shared — `sessionStorage` is per tab, which
   * is exactly why the ID lives there and the REGISTRY does not. Each entry carries a
   * heartbeat; a tab that has not checked in within `TAB_STALE_MS` is treated as closed,
   * because a browser gives no reliable "tab closed" signal (`beforeunload` does not fire on
   * a crash, a force-quit, or a Chromebook lid closing).
   */
  private liveTabs(): { sessionId: string; seen: number }[] {
    const cutoff = Date.now() - TAB_STALE_MS;
    let tabs: { sessionId: string; seen: number }[] = [];
    try {
      const raw = JSON.parse(localStorage.getItem(TAB_REGISTRY_KEY) || '[]');
      if (Array.isArray(raw)) {
        tabs = raw.filter(
          (t): t is { sessionId: string; seen: number } =>
            !!t && typeof t.sessionId === 'string' && typeof t.seen === 'number'
        );
      }
    } catch {
      // A corrupt registry must not sign anybody out. An empty list is the safe reading:
      // it permits, and the wall-clock expiry above is unaffected.
      return [];
    }
    return tabs.filter((t) => t.seen >= cutoff).sort((a, b) => a.seen - b.seen);
  }

  /** Record this tab, and refresh its heartbeat. */
  private registerTab(sessionId: string): void {
    try {
      const others = this.liveTabs().filter((t) => t.sessionId !== sessionId);
      const updated = [...others, { sessionId, seen: Date.now() }];
      localStorage.setItem(TAB_REGISTRY_KEY, JSON.stringify(updated));
    } catch {
      /* storage disabled: the limit simply is not enforced, which fails OPEN by design —
         locking a child out because their browser refuses storage would be worse than not
         enforcing a tab count. */
    }
  }

  /** This tab's own id, if it already has one. */
  private readTabSessionId(userId: string): string | null {
    try {
      const id = sessionStorage.getItem(TAB_SESSION_KEY);
      const owner = sessionStorage.getItem(TAB_SESSION_USER_KEY);
      // Same tab AND same person. A different uid means a different session, even in the
      // same tab and even if the previous sign-out never fired.
      if (id && id.length > 0 && owner === userId) return id;
      return null;
    } catch {
      return null;
    }
  }

  private createSession(user: User): void {
    /*
     * The session id is PER TAB (S33, LAUNCH_PLAN task 36).
     *
     * It used to be minted on every auth-state change and written to a SHARED
     * `localStorage['session_info']`, while `checkConcurrentSessions` signed the user out
     * whenever the stored id differed from the one in memory. Opening a second tab therefore
     * signed you out of BOTH within 60 seconds — and because Firebase auth persistence is
     * shared across tabs, the `signOut()` that followed was global: it did not evict "the
     * other" session, it ended both.
     *
     * On the hardware this product is sold for that was a routine action. A student opening
     * the editor in one tab and the curriculum in another was logged out mid-lesson, with a
     * red box telling them their session had been "replaced by a login in another tab".
     *
     * `sessionStorage` is per-tab by definition, so each tab now keeps its own id and no tab
     * can mistake another for a takeover. The registry below is what the CONFIGURED limit is
     * enforced against instead.
     */
    const sessionId = this.readTabSessionId(user.uid) ?? this.generateSessionId();
    try {
      sessionStorage.setItem(TAB_SESSION_KEY, sessionId);
      sessionStorage.setItem(TAB_SESSION_USER_KEY, user.uid);
    } catch {
      /* a tab with storage disabled still works; it simply re-mints its id on reload */
    }

    this.currentSession = {
      userId: user.uid,
      sessionId,
      loginTime: Date.now(),
      lastActivity: Date.now(),
      userAgent: navigator.userAgent,
      isActive: true
    };

    // Shared across tabs: the wall-clock expiry, which is a property of the LOGIN rather
    // than of any one tab.
    const sessionData = {
      ...this.currentSession,
      expiresAt: Date.now() + this.sessionSettings.maxSessionDuration
    };
    
    localStorage.setItem('session_info', JSON.stringify(sessionData));
    this.registerTab(sessionId);

    // Start inactivity monitoring
    this.startInactivityTimer();

    // Validate token freshness
    this.validateTokenFreshness(user);
  }

  /**
   * Destroy the current session
   */
  private destroySession(): void {
    /*
     * Retire this TAB's session id as well as the shared record.
     *
     * Without this a second person signing in on the same tab — a shared classroom
     * Chromebook, which is the machine this product is sold for — would inherit the previous
     * user's session id, because it is read back from `sessionStorage` on the next
     * `createSession`. Two different logins must never share one identifier.
     */
    const retiring = this.currentSession?.sessionId;

    this.currentSession = null;
    this.stopInactivityTimer();
    localStorage.removeItem('session_info');
    try {
      sessionStorage.removeItem(TAB_SESSION_KEY);
      sessionStorage.removeItem(TAB_SESSION_USER_KEY);
      if (retiring) {
        const remaining = this.liveTabs().filter((t) => t.sessionId !== retiring);
        localStorage.setItem(TAB_REGISTRY_KEY, JSON.stringify(remaining));
      }
    } catch {
      /* storage disabled — nothing to clean up */
    }
    
    // Clear any cached sensitive data
    this.clearSensitiveData();
  }

  /**
   * Generate a cryptographically secure session ID
   */
  private generateSessionId(): string {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Update last activity timestamp and reset inactivity timer
   */
  private updateLastActivity(): void {
    if (this.currentSession) {
      this.currentSession.lastActivity = Date.now();
      this.resetInactivityTimer();
      
      // Update localStorage
      const sessionData = JSON.parse(localStorage.getItem('session_info') || '{}');
      sessionData.lastActivity = Date.now();
      localStorage.setItem('session_info', JSON.stringify(sessionData));
    }
  }

  /**
   * Start the inactivity timer
   */
  private startInactivityTimer(): void {
    this.inactivityTimerStartedAt = Date.now();
    this.inactivityTimer = setTimeout(() => {
      this.handleInactivityTimeout();
    }, this.sessionSettings.inactivityTimeout);
  }

  /**
   * Reset the inactivity timer
   */
  private resetInactivityTimer(): void {
    this.stopInactivityTimer();
    this.startInactivityTimer();
  }

  /**
   * Stop the inactivity timer
   */
  private stopInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  /**
   * Pause inactivity timer (when tab is not visible) — records elapsed time
   */
  private pauseInactivityTimer(): void {
    this.stopInactivityTimer();
    // inactivityTimerStartedAt is preserved so resumeInactivityTimer can
    // calculate the remaining time correctly.
  }

  /**
   * Resume inactivity timer with the remaining time (not the full timeout)
   */
  private resumeInactivityTimer(): void {
    if (!this.currentSession || !this.isSessionValid()) return;

    const elapsed = this.inactivityTimerStartedAt
      ? Date.now() - this.inactivityTimerStartedAt
      : 0;
    const remaining = this.sessionSettings.inactivityTimeout - elapsed;

    if (remaining <= 0) {
      this.handleInactivityTimeout();
      return;
    }

    this.inactivityTimerStartedAt = Date.now() - elapsed; // keep start consistent
    this.inactivityTimer = setTimeout(() => {
      this.handleInactivityTimeout();
    }, remaining);
  }

  /**
   * Handle inactivity timeout
   */
  private handleInactivityTimeout(): void {
    console.warn('Session expired due to inactivity');
    
    // Show user-friendly message
    this.showSessionExpiredMessage('Your session has expired due to inactivity. Please log in again.');
    
    // Sign out the user
    signOutKeepingWork().catch(error => {
      console.error('Error signing out after inactivity:', error);
    });
  }

  /**
   * Validate session integrity and expiration
   */
  public isSessionValid(): boolean {
    if (!this.currentSession) {
      return false;
    }

    const now = Date.now();
    const sessionAge = now - this.currentSession.loginTime;
    const timeSinceActivity = now - this.currentSession.lastActivity;

    // Check session age
    if (sessionAge > this.sessionSettings.maxSessionDuration) {
      console.warn('Session expired due to maximum duration');
      this.handleSessionExpiration('Session expired. Please log in again.');
      return false;
    }

    // Check inactivity timeout
    if (timeSinceActivity > this.sessionSettings.inactivityTimeout) {
      console.warn('Session expired due to inactivity');
      this.handleSessionExpiration('Session expired due to inactivity. Please log in again.');
      return false;
    }

    return true;
  }

  /**
   * Handle session expiration
   */
  private handleSessionExpiration(message: string): void {
    this.showSessionExpiredMessage(message);
    signOutKeepingWork().catch(error => {
      console.error('Error signing out after session expiration:', error);
    });
  }

  /**
   * Show session expired message to user
   */
  private showSessionExpiredMessage(message: string): void {
    // Create a simple modal or use existing notification system
    const notification = document.createElement('div');
    /*
     * S33 noted this and left it: the box was a bare `div`, so a screen-reader user was
     * never told their session had ended. They simply found themselves signed out, with the
     * only explanation painted on screen where they could not reach it.
     *
     * `role="alert"` is correct here rather than `status`: the session is already over and
     * whatever they were typing is gone, which is worth interrupting for. `aria-live` is
     * stated as well because the element is inserted AFTER the page has loaded, and some
     * screen readers announce a late-added `role="alert"` more reliably when both are
     * present.
     */
    notification.setAttribute('role', 'alert');
    notification.setAttribute('aria-live', 'assertive');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ff6b6b;
      color: white;
      padding: 15px;
      border-radius: 5px;
      z-index: 10000;
      max-width: 300px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    `;
    notification.textContent = message;
    
    document.body.appendChild(notification);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (notification.parentNode) {
        notification.parentNode.removeChild(notification);
      }
    }, 5000);
  }

  /**
   * Validate token freshness and force refresh if needed
   */
  private async validateTokenFreshness(user: User): Promise<void> {
    try {
      // Force token refresh if token is older than 1 hour
      const tokenResult = await user.getIdTokenResult();
      const tokenAge = Date.now() - new Date(tokenResult.authTime).getTime();
      
      if (tokenAge > 60 * 60 * 1000) { // 1 hour
        await user.getIdToken(true); // Force refresh
      }
    } catch (error) {
      console.error('Error validating token freshness:', error);
      // If token validation fails, sign out for security
      signOutKeepingWork().catch(signOutError => {
        console.error('Error signing out after a failed token check:', signOutError);
      });
    }
  }

  /**
   * Check session validity and expiry. True multi-tab/concurrent session
   * enforcement requires server-side tracking; this provides client-side
   * enforcement for single-device scenarios.
   */
  private checkConcurrentSessions(): void {
    const sessionData = localStorage.getItem('session_info');
    if (!sessionData) return;

    try {
      const session = JSON.parse(sessionData);
      const now = Date.now();

      // Session expired by wall-clock limit
      if (session.expiresAt && now > session.expiresAt) {
        this.handleSessionExpiration('Your session has expired. Please log in again.');
        return;
      }

      /*
       * A second tab is NOT a takeover (S33, task 36).
       *
       * This used to sign the user out whenever the shared session id differed from this
       * tab's. What it enforces now is the limit the configuration has always DECLARED and
       * never applied: `maxConcurrentSessions`, which said five while the code permitted
       * exactly one — a setting that named a policy and enforced nothing.
       *
       * Honest about what this can and cannot do: Firebase auth persistence is shared
       * across tabs, so a client-side sign-out always ends every tab. There is no way from
       * here to evict one and keep the others, and pretending otherwise is what produced the
       * original bug. So the limit is enforced by refusing to open MORE than the configured
       * number, and the tab that trips it is the one that stops — not the ones already in
       * use, which belong to work the person is in the middle of.
       */
      const live = this.liveTabs();
      if (live.length > this.sessionSettings.maxConcurrentSessions) {
        const mine = this.currentSession?.sessionId;
        const oldestAllowed = live.slice(0, this.sessionSettings.maxConcurrentSessions);
        if (mine && !oldestAllowed.some((t) => t.sessionId === mine)) {
          this.handleSessionExpiration(
            `You have this app open in more than ${this.sessionSettings.maxConcurrentSessions} ` +
              'places. Close one of the others and sign in again here.'
          );
        }
      }
    } catch (error) {
      console.error('Error checking session validity:', error);
    }
  }

  /**
   * Clear sensitive data from memory and storage
   */
  private clearSensitiveData(): void {
    // Clear any cached API keys or sensitive data
    const sensitiveKeys = [
      'cached_api_key',
      'user_preferences',
      'project_cache'
    ];
    
    sensitiveKeys.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
  }

  /**
   * Get current session information
   */
  public getCurrentSession(): SessionInfo | null {
    return this.currentSession;
  }

  /**
   * Extend session (reset timers)
   */
  public extendSession(): boolean {
    if (this.isSessionValid()) {
      this.updateLastActivity();
      return true;
    }
    return false;
  }

  /**
   * Force logout with security cleanup
   */
  public secureLogout(): void {
    
    // Clear all sensitive data
    this.clearSensitiveData();
    
    // Destroy session
    this.destroySession();
    
    // Sign out from Firebase
    signOutKeepingWork().catch(error => {
      console.error('Error during secure logout:', error);
    });
  }

  /**
   * Get session security metrics
   */
  public getSecurityMetrics(): {
    sessionAge: number;
    timeSinceActivity: number;
    isValid: boolean;
    remainingTime: number;
    inactivityTimeout: number;
  } | null {
    if (!this.currentSession) {
      return null;
    }

    const now = Date.now();
    return {
      sessionAge: now - this.currentSession.loginTime,
      timeSinceActivity: now - this.currentSession.lastActivity,
      isValid: this.isSessionValid(),
      remainingTime: Math.max(0, this.sessionSettings.maxSessionDuration - (now - this.currentSession.loginTime)),
      inactivityTimeout: this.sessionSettings.inactivityTimeout
    };
  }
}

// Global session security manager instance
export const sessionSecurity = new SessionSecurityManager();

// Export types for external use
export type { SessionInfo, SessionSettings };